import { useMemo } from 'react';

import { defaultBreakpointsDetails } from './defaultConfig';
import { useMediaQuery } from './useMediaQuery';
import { getBreakpointsDetails } from './utils/getBreakpointsDetails';

export type UseBreakpointOptions = {
  /** The fallback breakpoint in a non browser context */
  fallback?: string;
  /** Custom set of breakpoints */
  breakpoints?: Record<string, string>;
};

/**
 * Use breakpoint
 *
 * A hook used to get the current media query breakpoint
 *
 * @example
 *   const breakpoint = useBreakpoint();
 *   console.log(breakpoint); // eg. 'sm'
 *
 *   const breakpointWithSRRFallback = useBreakpoint({ fallback: 'lg' });
 *
 *   const breakpointWithCustomBreakpoints = useBreakpoints({
 *     breakpoints: { sm: '400px' },
 *   });
 *
 *   console.log(breakpointWithCustomBreakpoints); // 'base' || 'sm'
 */
export function useBreakpoint(arg?: UseBreakpointOptions): string {
  const { breakpoints, fallback } = arg ?? {};

  const breakpointDetails = useMemo(() => {
    if (!breakpoints) {
      return defaultBreakpointsDetails;
    }

    return getBreakpointsDetails(breakpoints);
  }, [breakpoints]);

  const mediaQueries = useMemo(() => {
    return breakpointDetails.breakpoints.map(
      (breakpointDetail) => breakpointDetail.minMaxQuery,
    );
  }, [breakpointDetails]);

  const fallbackValues = useMemo(() => {
    return breakpointDetails.breakpoints.map(
      (breakpointDetail) => breakpointDetail.breakpoint === fallback,
    );
  }, [breakpointDetails, fallback]);

  const currentMediaQueryValues = useMediaQuery(mediaQueries, {
    fallback: fallbackValues,
  });

  const currentMediaQueryIndex = currentMediaQueryValues.findIndex(
    (mediaQuery) => !!mediaQuery,
  );

  return (
    breakpointDetails.breakpoints[currentMediaQueryIndex]?.breakpoint ??
    fallback
  );
}
